'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import React from 'react';

import type { Blog } from '@eliadmin/core/src/api/blogs/blogs-types';

import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { buttonVariants } from '../ui/button';
import { RxArrowTopRight } from 'react-icons/rx';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { Badge } from '../ui/badge';

export type BlogCardProps = {
  blog: Blog;
  headingTag?: 'h2' | 'h3';
};

export function BlogCard({ blog, headingTag: HeadingTag = 'h3' }: BlogCardProps) {
  const locale = useLocaleStore(state => state.currentLocale);
  const { t } = useTranslations();

  let formattedDate;

  try {
    formattedDate = new Date(blog.created_at).toLocaleDateString(locale);
  } catch (error) {
    if (error instanceof RangeError) {
      formattedDate = new Date(blog.created_at).toLocaleDateString('en-US');
    } else {
      throw error;
    }
  }

  return (
    <Link
      prefetch={false}
      href={ROUTES.BLOG_DETAIL({ slug: blog.slug })}
      className={cn(
        'group transition-all',
        'relative col-span-1 flex h-[380px] w-full items-end justify-end overflow-hidden rounded-[20px] bg-white p-4 sm:h-[420px] lg:h-[450px]',
      )}
    >
      {!!blog.categories.length && (
        <Badge size={'sm'} variant={'white'} className="absolute left-4 top-4 z-[5]">
          {blog?.categories[0]?.name}
        </Badge>
      )}
      <div className="relative z-[3] flex w-full flex-col items-start justify-start gap-4 sm:gap-5 lg:gap-6">
        <div className="flex w-full flex-col items-start justify-start gap-2">
          <p
            className={cn(
              'text-xs sm:text-sm',
              'text-black/50 transition-all group-hover:text-white/70',
            )}
          >
            {formattedDate}
          </p>
          <HeadingTag
            data-ea-visual-tag="h4"
            className={cn(
              'line-clamp-2 h-[46px] text-xl font-medium leading-[1.15] tracking-tight lg:h-14 lg:text-2xl lg:leading-[1.15]',
              'text-black transition-all group-hover:text-white',
            )}
          >
            {blog.name}
          </HeadingTag>
        </div>
        <div className="flex items-center justify-start gap-2.5">
          <span
            aria-hidden="true"
            data-ea-visual-tag="button"
            className={cn(
              buttonVariants({ size: 'icon' }),
              'bg-[#F7F7F2] text-sm font-semibold text-black hover:bg-[#F7F7F2] group-hover:bg-white/20 group-hover:text-white group-hover:hover:bg-white/30 sm:text-base',
            )}
          >
            <RxArrowTopRight size={20} />
          </span>
          <p
            className={cn(
              'text-sm font-semibold text-black group-hover:text-white sm:text-base',
              'transition-all',
            )}
          >
            {t('text1148')}
          </p>
        </div>
      </div>
      <div
        className={cn(
          'bg-gradient-to-b from-black/5 to-black/70',
          'absolute left-0 top-0 h-full w-full rounded-b-[20px] opacity-0',
          'z-[2] max-h-[220px] transition-all duration-300 group-hover:max-h-[380px] group-hover:opacity-100 sm:max-h-[240px] sm:group-hover:max-h-[420px] lg:max-h-[260px] lg:group-hover:max-h-[450px]',
        )}
      />
      <Image
        src={blog?.image?.url ?? '/images/placeholder.png'}
        alt={resolveImageAlt(blog?.image, blog?.name)}
        width={0}
        height={0}
        className={cn(
          'absolute left-0 top-0 h-[220px] w-full rounded-b-[20px] object-cover sm:h-[240px] lg:h-[260px]',
          'z-0 group-hover:h-[380px] sm:group-hover:h-[420px] lg:group-hover:h-[450px]',
          'transition-all duration-300',
        )}
        sizes="(max-width: 640px) 100vw, (max-width: 768px) 50vw, 33vw"
      />
    </Link>
  );
}
